import { HomeLayout } from "layouts";
import { useGQLSWR, fetcher, imageUrl, translate } from "utils";
import { gql } from "graphql-request";
import { Section, Container, Button, Icon, ButtonLink } from "components";
import Link from "next/link";
import Image from "next/legacy/image";
import Head from "next/head";
import { useRouter } from "next/router";
import * as cx from "classnames/bind";
import s from "styles/Journal.module.scss";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { useTranslation } from "next-i18next";
import urlSlug from "url-slug";

function Category({ articles, categories, category, posts, cats }) {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const regex = /(<([^>]+)>)/gi;
  return (
    <HomeLayout className={cx(s.categoryHolder)} style={{ paddingTop: "0px" }}>
      <Head>
        <title key="title">{category.name} - Cocktail Club Journal</title>
        <meta
          key="description"
          name="description"
          content="What’s happening in the cocktail world? Plunge into our articles and get to feel the cocktail universe? We have gathered related content and tips & tricks to get you started making cocktails and living the fabulous life of cocktailclub.com."
        />
        <meta
          key="ogUrl"
          property="og:url"
          content={"https://cocktailclub.com/journal/" + category.slug}
        />
        <link
          key="canonical"
          rel="canonical"
          href={"https://cocktailclub.com/journal/" + category.slug}
        />

        <meta key="ogType" property="og:type" content="website" />
        <meta
          key="ogTitle"
          property="og:title"
          content={category.name + " - Cocktail Club Journal"}
        />
        <meta
          key="ogDescription"
          property="og:description"
          content="What’s happening in the cocktail world? Plunge into our articles and get to feel the cocktail universe? We have gathered related content and tips & tricks to get you started making cocktails and living the fabulous life of cocktailclub.com."
        />

        <meta
          key="ogImage"
          property="og:image"
          content="https://cocktailclub.com/img/journal/category.jpg"
        />
        <meta
          property="og:image:alt"
          content={category.name + " - Cocktail Club Journal"}
        />
      </Head>
      <Section className="p0">
        <div className={cx("JournalFeedHeader", s.JournalFeedHeader)}>
          <nav className={cx(s.Breadcrumbs)}>
            <Container>
              <span>
                <Link href="/journal">{t("Journal")}</Link>
              </span>
              <span>{translate(category, "name", locale)}</span>
            </Container>
          </nav>
          <Container>
            <h1 className="Heading__main Brick Light">
              {translate(category, "name", locale)}
            </h1>
            <h2>
              {t(
                "What’s happening in the cocktail world? Plunge into our articles and get to feel the cocktail universe? We have gathered related content and tips & tricks to get you started making cocktails and living the fabulous life of cocktailclub.com.",
              )}
            </h2>
            <br />
            <div className={cx(s.catNav)}>
              {categories.map((category) => (
                <Link
                  className="Bold"
                  key={category.id}
                  href={"/journal/" + urlSlug(category.slug)}
                >
                  {translate(category, "name", locale)}
                </Link>
              ))}
              <Link className="Bold" href="/z2h">
                Zero to Hero
              </Link>
            </div>
          </Container>
        </div>
      </Section>
      <Section className={cx(s.JournalFirstHeader)}>
        <Container>
          <h2 className="Heading__styled my32">{t("Zero to hero")}</h2>
          <h2>
            Welcome to your guide through the Cocktail Club bar tools. Take a
            tour and learn about and how to use the Cocktail Club bar tools and
            of course the Cocktail Club Bar Kit.
          </h2>
          <ButtonLink href="/z2h" className="Primary Button">
            Go to Zero to Hero
          </ButtonLink>
          <br />
          <br />
          <h2 className="Heading__styled my32">{t("Recent articles")}</h2>
          <div className={cx("JournalFeed row flex flex-wrap")}>
            {posts.map((post, index) => (
              <div
                className={cx("JournalFeedItem rowItem", s.JournalFeedItem)}
                key={index}
              >
                <Link
                  href={
                    "/journal/" +
                    urlSlug(post.categories[0].category.slug) +
                    "/" +
                    post.slug
                  }
                >
                  <Image
                    src={imageUrl(post.image_id || "")}
                    layout="responsive"
                    width={350}
                    height={420}
                    objectFit="cover"
                    itemProp="image"
                    alt={post.title}
                  />
                  <header>
                    <p>
                      {post.categories.map((category, index) => (
                        <Link
                          href={"/journal/" + urlSlug(category.category.slug)}
                          key={category.id}
                        >
                          {translate(category.category, "name", locale)}
                          {index !== post.categories.length - 1 && ", "}
                        </Link>
                      ))}
                    </p>
                    <h3 className="title">
                      {translate(post, "title", locale)}
                    </h3>
                    {post.excerpt.length && (
                      <span>
                        {translate(post, "excerpt", locale)
                          .replace(regex, "")
                          .slice(0, 122) + "..."}
                      </span>
                    )}
                  </header>
                </Link>
              </div>
            ))}
          </div>
        </Container>
      </Section>
    </HomeLayout>
  );
}

export async function getStaticProps({ locale, params: { category } }) {
  const query = gql`
    query {
      articles(where: { hidden: { _eq: false }, categories: {category: {slug: {_eq: "${category}"}}}}) {
        id
        title
        slug
        excerpt
        image_id
        hidden
        translations
        categories {
          category {
            name
            slug
            translations
          }
        }
      }
      category: article_categories(where: {slug: {_eq: "${category}"}}) {
        id
        name
        slug
        translations
      }
      categories: article_categories(
        where: {
          _not: { articles_aggregate: { count: { predicate: { _eq: 0 } } } }
        }
      ) {
        id
        name
        slug
        translations
      }
    }
  `;

  const data = await fetcher(query);
  const articles = data.articles;
  if (articles.length < 1) {
    return {
      notFound: true,
    };
  }
  const cats = [...new Set(articles.map((p) => p.categories[0].category.name))];

  return {
    props: {
      ...data,
      cats,
      category: data.category[0],
      posts: articles,
      ...(await serverSideTranslations(locale)),
    },
    revalidate: 60,
  };
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export default Category;
